// frontend/src/pages/SingleBlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import API from '../services/api';

const SingleBlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await API.blog.getPost(slug);
                if (response.success) {
                    setPost(response.data);
                } else {
                    setError('Post not found');
                }
            } catch (err) {
                console.error('Error fetching post:', err);
                setError('Failed to load post. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchPost();

        // Scroll to top when post loads
        window.scrollTo(0, 0);
    }, [slug]);

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen pt-20 bg-gray-50 dark:bg-gray-900">
                <div className="container mx-auto px-6 py-16">
                    <div className="max-w-4xl mx-auto">
                        <div className="animate-pulse">
                            <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
                            <div className="h-96 bg-gray-300 dark:bg-gray-700 rounded-3xl mb-8"></div>
                            <div className="space-y-4">
                                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-5/6"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error State
    if (error || !post) {
        return (
            <div className="min-h-screen pt-20 bg-gray-50 dark:bg-gray-900">
                <div className="container mx-auto px-6 py-16">
                    <div className="max-w-2xl mx-auto text-center">
                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-12 shadow-lg">
                            <svg className="w-20 h-20 mx-auto mb-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h1 className="font-playfair text-3xl font-bold text-gray-800 dark:text-white mb-4">
                                Post Not Found
                            </h1>
                            <p className="font-graphik text-gray-600 dark:text-gray-300 mb-8">
                                {error || 'The blog post you\'re looking for doesn\'t exist or has been removed.'}
                            </p>
                            <Link
                                to="/blog"
                                className="inline-flex items-center space-x-2 bg-emerald-600 text-white px-6 py-3 rounded-xl font-graphik font-medium hover:bg-emerald-700 transition-colors duration-300"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                <span>Back to Blog</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Main Post Content
    return (
        <div className="min-h-screen pt-20 bg-gray-50 dark:bg-gray-900">
            {/* Breadcrumb Navigation */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="container mx-auto px-6 py-4">
                    <nav className="flex items-center space-x-2 text-sm font-graphik">
                        <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                            Home
                        </Link>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <Link to="/blog" className="text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                            Blog
                        </Link>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">
                            {post.title}
                        </span>
                    </nav>
                </div>
            </div>

            {/* Article Header */}
            <article className="container mx-auto px-6 py-12">
                <div className="max-w-4xl mx-auto">
                    {/* Category & Back Button */}
                    <div className="flex items-center justify-between mb-6">
                        <span className="inline-flex items-center bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-graphik font-medium uppercase tracking-wide">
                            {post.category}
                        </span>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors font-graphik"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            <span>Back</span>
                        </button>
                    </div>

                    {/* Title */}
                    <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-emerald-800 dark:text-white mb-6 leading-tight">
                        {post.title}
                    </h1>

                    {/* Meta Information */}
                    <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                                <span className="font-playfair text-emerald-600 dark:text-emerald-400 font-bold text-lg">
                                    {post.author_name.charAt(0)}
                                </span>
                            </div>
                            <div>
                                <p className="font-graphik font-medium text-gray-900 dark:text-white">
                                    {post.author_name}
                                </p>
                                <p className="font-graphik text-sm text-gray-500 dark:text-gray-400">
                                    Author
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 font-graphik">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{post.published_date}</span>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 font-graphik">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{post.reading_time}</span>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 font-graphik">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>{post.views.toLocaleString()} views</span>
                        </div>
                    </div>

                    {/* Featured Image */}
                    {post.image_url && (
                        <div className="relative rounded-3xl overflow-hidden mb-12 shadow-2xl">
                            <img
                                src={post.image_url}
                                alt={post.title}
                                className="w-full h-auto object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        </div>
                    )}

                    {/* Article Content */}
                    <div
                        className="prose prose-lg dark:prose-invert max-w-none mb-12
              prose-headings:font-playfair prose-headings:text-emerald-800 dark:prose-headings:text-emerald-400
              prose-p:font-graphik prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed
              prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-emerald-700 dark:prose-strong:text-emerald-300
              prose-blockquote:border-l-4 prose-blockquote:border-emerald-600 prose-blockquote:bg-emerald-50 dark:prose-blockquote:bg-emerald-900/20 prose-blockquote:p-4 prose-blockquote:rounded-r-xl
              prose-img:rounded-2xl prose-img:shadow-lg
              prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-code:bg-emerald-50 dark:prose-code:bg-emerald-900/30 prose-code:px-2 prose-code:py-1 prose-code:rounded"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                        <div className="mb-12 pb-12 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-playfair text-lg font-bold text-gray-900 dark:text-white mb-4">Tags</h3>
                            <div className="flex flex-wrap gap-3">
                                {post.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-4 py-2 rounded-full text-sm font-graphik font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors cursor-pointer"
                                    >
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Post Navigation */}
                    {(post.previous_post || post.next_post) && (
                        <div className="mb-16 pb-12 border-b border-gray-200 dark:border-gray-700">
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Previous Post */}
                                {post.previous_post ? (
                                    <Link
                                        to={`/blog/${post.previous_post.slug}`}
                                        className="group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
                                    >
                                        <div className="flex items-center p-6">
                                            <div className="flex-shrink-0 mr-4">
                                                <svg className="w-10 h-10 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-graphik text-gray-500 dark:text-gray-400 mb-1">Previous Post</p>
                                                <h4 className="font-playfair text-lg font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2">
                                                    {post.previous_post.title}
                                                </h4>
                                            </div>
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="bg-gray-100 dark:bg-gray-800/50 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 opacity-50">
                                        <p className="text-sm font-graphik text-gray-400 dark:text-gray-500 text-center">No previous post</p>
                                    </div>
                                )}

                                {/* Next Post */}
                                {post.next_post ? (
                                    <Link
                                        to={`/blog/${post.next_post.slug}`}
                                        className="group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
                                    >
                                        <div className="flex items-center p-6">
                                            <div className="flex-1 min-w-0 text-right">
                                                <p className="text-sm font-graphik text-gray-500 dark:text-gray-400 mb-1">Next Post</p>
                                                <h4 className="font-playfair text-lg font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2">
                                                    {post.next_post.title}
                                                </h4>
                                            </div>
                                            <div className="flex-shrink-0 ml-4">
                                                <svg className="w-10 h-10 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="bg-gray-100 dark:bg-gray-800/50 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 opacity-50">
                                        <p className="text-sm font-graphik text-gray-400 dark:text-gray-500 text-center">No next post</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Share Section */}
                    <div className="mb-16">
                        <h3 className="font-playfair text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Share This Story</h3>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}
                                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-graphik font-medium hover:bg-blue-700 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                                <span>Share</span>
                            </button>

                            <button
                                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(post.title)}`, '_blank')}
                                className="flex items-center space-x-2 bg-sky-500 text-white px-6 py-3 rounded-xl font-graphik font-medium hover:bg-sky-600 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                </svg>
                                <span>Tweet</span>
                            </button>

                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.href);
                                    alert('Link copied to clipboard!');
                                }}
                                className="flex items-center space-x-2 bg-gray-600 text-white px-6 py-3 rounded-xl font-graphik font-medium hover:bg-gray-700 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                </svg>
                                <span>Copy Link</span>
                            </button>
                        </div>
                    </div>
                </div>
            </article>

            {/* Related Posts */}
            {post.related_posts && post.related_posts.length > 0 && (
                <section className="bg-white dark:bg-gray-800 py-16">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="section-overline mb-4">
                                    <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                                        Keep Reading
                                    </span>
                                </div>
                                <h2 className="font-playfair text-3xl md:text-4xl font-bold text-emerald-800 dark:text-white">Related Stories</h2>
                            </div>

                            <div className="grid md:grid-cols-3 gap-8">
                                {post.related_posts.map((relatedPost) => (
                                    <Link
                                        key={relatedPost.id}
                                        to={`/blog/${relatedPost.slug}`}
                                        className="group bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
                                    >
                                        <div className="relative overflow-hidden h-48">
                                            <img
                                                src={relatedPost.image_url}
                                                alt={relatedPost.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                            <div className="absolute top-3 left-3">
                                                <span className="bg-emerald-600 text-white px-3 py-1 rounded-lg text-xs font-graphik font-medium uppercase tracking-wide">
                                                    {relatedPost.category}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-6">
                                            <p className="text-sm font-graphik text-gray-500 dark:text-gray-400 mb-2">
                                                {relatedPost.published_date}
                                            </p>
                                            <h3 className="font-playfair text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2">
                                                {relatedPost.title}
                                            </h3>
                                            <p className="font-graphik text-gray-600 dark:text-gray-300 text-sm line-clamp-3">
                                                {relatedPost.excerpt}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section className="bg-gradient-to-br from-emerald-600 to-emerald-800 py-16">
                <div className="container mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready for Your Adventure?
                        </h2>
                        <p className="font-graphik text-white/90 text-lg mb-8">
                            Book your ATV tour today and experience the thrill of St. Vincent's beautiful landscapes
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/tours"
                                className="inline-flex items-center justify-center space-x-2 bg-white text-emerald-600 px-8 py-4 rounded-xl font-graphik font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <span>Book Tours</span>
                            </Link>

                            <Link
                                to="/blog"
                                className="inline-flex items-center justify-center space-x-2 bg-emerald-700 text-white px-8 py-4 rounded-xl font-graphik font-bold text-lg hover:bg-emerald-800 transition-colors duration-300"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                <span>More Stories</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SingleBlogPost;